import { configureStore } from '@reduxjs/toolkit'
import { isEmpty } from 'lodash'
import { combineReducers, StateFromReducersMapObject } from 'redux'
import {
  FLUSH,
  PAUSE,
  PERSIST,
  Persistor,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { withReduxConfigs } from 'utils'

import filterReducer from '~map-filter-view/states/filter'
import mapApi from '~map-preview/services/map'
import boundariesReducer from './boundaries'
import hexagonsReducer from './hexagons'
import infographicReducer from './infographic'
import layerReducer from './layer'
import layerTypeReducer from './layerType'
import legendReducer from './legend'
import mapReducer, { initialMapState } from './map'
import mapShareReducer from './mapShare'
import markerReducer from './marker'
import uiReducer, { initialUiState } from './ui'
import viewStateReducer from './viewState'

import { reopenMapLegendIf, uiListener } from './ui/middleware'

const reducers = {
  boundaries: boundariesReducer,
  filter: filterReducer,
  hexagons: hexagonsReducer,
  layer: layerReducer,
  layerType: layerTypeReducer,
  legend: legendReducer,
  map: mapReducer,
  marker: markerReducer,
  ui: uiReducer,
  viewState: viewStateReducer,
  mapShare: mapShareReducer,
  infographic: infographicReducer,
  [mapApi.reducerPath]: mapApi.reducer
}

export type RootState = StateFromReducersMapObject<typeof reducers>

let store
let persistor: Persistor

class MapIdRequiredError extends Error {
  constructor() {
    super('mapId is required to create a store.')
    this.name = 'MapIdRequiredError'
  }
}

function getMapState(map) {
  return {
    ...initialMapState,
    mapData: map,
    isMapSaved: !isEmpty(map)
  }
}

function getUIState(map) {
  return {
    ...initialUiState,
    open: {
      ...initialUiState.open,
      MapDescriptionDrawer: !isEmpty(map)
    }
  }
}

function createStore(map = {}, mapId, userId = 'default') {
  if (!mapId) throw new MapIdRequiredError()

  const reducer = persistReducer(
    {
      keyPrefix: '',
      key: `map:${userId}:${mapId}`,
      storage,
      whitelist: ['boundaries', 'hexagons', 'layerType', 'markers', 'viewState']
    },
    combineReducers(reducers)
  )

  store = configureStore(
    withReduxConfigs({
      reducer,
      middleware: (getDefaultMiddleware) => {
        return getDefaultMiddleware({
          serializableCheck: {
            ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
          }
        }).concat([mapApi.middleware, uiListener.middleware])
      },
      preloadedState: {
        map: getMapState(map),
        ui: getUIState(map)
      }
    })
  )

  persistor = persistStore(
    store,
    {
      manualPersist: true
    } as any,
    () => {
      reopenMapLegendIf()
    }
  )

  return { store, persistor }
}

export { createStore, persistor, store }
